import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import StrategyCard from "../components/StrategyCard";
import { Plus } from "lucide-react";

function enrich(strategies, userId) {
  return strategies.map((s) => ({
    ...s,
    like_count:  s.strategy_likes?.length || 0,
    liked_by_me: s.strategy_likes?.some((l) => l.user_id === userId) || false,
  }));
}

export default function Strategies() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [sort, setSort] = useState(params.get("sort") || "recent");
  const [tag,  setTag]  = useState(params.get("tag")  || "");
  const [q,    setQ]    = useState(params.get("q")    || "");

  useEffect(() => {
    let query = supabase
      .from("strategies")
      .select("*, author:profiles!user_id(id,name,avatar_url,participant_id), strategy_likes(user_id)")
      .limit(50);

    if (tag)  query = query.contains("tags", [tag]);
    if (sort === "top") query = query.order("shares", { ascending: false });
    else                query = query.order("created_at", { ascending: false });

    query.then(({ data }) => {
      let enriched = enrich(data || [], user?.id);
      if (q) {
        const ql = q.toLowerCase().replace("#", "");
        enriched = enriched.filter((s) =>
          s.title.toLowerCase().includes(ql) ||
          s.summary.toLowerCase().includes(ql) ||
          (s.tags || []).some((t) => t.includes(ql))
        );
      }
      if (sort === "top") enriched.sort((a, b) => (b.like_count + b.shares) - (a.like_count + a.shares));
      setItems(enriched);
    });

    setParams({ ...(q ? { q } : {}), ...(tag ? { tag } : {}), sort });
  }, [sort, tag, q, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build dynamic tag list from all strategies in the database
  useEffect(() => {
    supabase.from("strategies").select("tags").then(({ data }) => {
      const tagSet = new Set();
      (data || []).forEach((s) => (s.tags || []).forEach((t) => tagSet.add(t)));
      // Sort alphabetically so the filter is predictable
      setAllTags([...tagSet].sort());
    });
  }, []);

  const onLike = async (s) => {
    if (!user) return;
    const wasLiked = s.liked_by_me;
    const patch = (x) => x.id !== s.id ? x : { ...x, liked_by_me: !wasLiked, like_count: wasLiked ? x.like_count - 1 : x.like_count + 1 };
    setItems((prev) => prev.map(patch));
    if (wasLiked) {
      await supabase.from("strategy_likes").delete().eq("strategy_id", s.id).eq("user_id", user.id);
    } else {
      await supabase.from("strategy_likes").insert({ strategy_id: s.id, user_id: user.id });
    }
  };

  const onShare = async (s) => {
    if (!user) return;
    try {
      await supabase.from("strategy_shares").insert({ strategy_id: s.id, user_id: user.id });
      await supabase.from("strategies").update({ shares: (s.shares || 0) + 1 }).eq("id", s.id);
      setItems((prev) => prev.map((x) => x.id === s.id ? { ...x, shares: (x.shares || 0) + 1 } : x));
    } catch (_) {}
    try { await navigator.clipboard.writeText(`${window.location.origin}/s/${s.id}`); } catch (_) {}
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
        <h1 className="font-display font-extrabold text-5xl uppercase tracking-tight">
          All <span className="highlight-marker">strategies</span>
        </h1>
        <Link to="/strategies/new" className="brutal-btn brutal-btn-yellow" data-testid="strategies-new-btn">
          <Plus size={14} /> Post strategy
        </Link>
      </div>

      <div className="brutal-card p-4 mb-6 flex flex-wrap gap-3 items-center">
        <input className="brutal-input flex-1 min-w-[200px]" placeholder="Search…" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="strategies-search-input" />
        <div className="flex gap-2">
          <button onClick={() => setSort("recent")} className={`brutal-btn text-xs ${sort === "recent" ? "brutal-btn-yellow" : ""}`}>Recent</button>
          <button onClick={() => setSort("top")} className={`brutal-btn text-xs ${sort === "top" ? "brutal-btn-red" : ""}`}>Top</button>
        </div>
      </div>

      {/* Dynamic tag filter — built from actual post data */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setTag("")} className={`tag-pill ${!tag ? "" : "opacity-60"}`} data-testid="tag-filter-all">All</button>
        {(showAllTags ? allTags : allTags.slice(0, 6)).map((t) => (
          <button key={t} onClick={() => setTag(t === tag ? "" : t)} className="tag-pill"
            style={{ background: tag === t ? "#FF3333" : "#FFD600", color: tag === t ? "#fff" : "#121212" }}
            data-testid={`tag-filter-${t}`}>
            #{t}
          </button>
        ))}
        {allTags.length > 6 && (
          <button
            onClick={() => setShowAllTags(!showAllTags)}
            className="tag-pill font-mono text-[11px]"
            style={{ background: "transparent", color: "#121212", border: "2px dashed #121212" }}
          >
            {showAllTags ? "View less ↑" : `+${allTags.length - 6} more ↓`}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="brutal-card p-8 font-hand text-2xl">No strategies match — yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((s) => <StrategyCard key={s.id} s={s} onLike={onLike} onShare={onShare} />)}
        </div>
      )}
    </main>
  );
}
